import React, {
    Dispatch, FC, ReactElement, SetStateAction, useEffect, useState, useCallback, useReducer
    , useRef
} from 'react'
import ActionToggle from 'live/components/ui/action-toggle'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import globalNotification from 'live/services/global-notification'
import styled from 'styled-components'

const i18n = require('live/services/i18n')
const xhr = require('live/services/xhr')

const RESOURCE_URL = '/services/plugin-upload/features/'

interface FeatureFlags {
    enableSFTP: boolean,
    enablePublicApi: boolean,
    enableAuthenticatedApi: boolean,
}

const initialState: FeatureFlags = {
    enableSFTP: true,
    enablePublicApi: true,
    enableAuthenticatedApi: true,
}

interface State<T> {
    open: boolean
    saved: boolean
    mustSave: boolean
    error: string | null
    persistedData: T
    submitted: boolean
}

interface Actions<T> {
    toggle: () => void
    submit: () => void
    saveSuccess: (persistedData: T) => void
    setError: (error: string) => void
    setPersistedData: (persistedData: T) => void
}

enum ACTION_TYPES {
    TOGGLE = 'TOGGLE',
    SUBMIT = 'SUBMIT',
    SAVE_SUCCESS = 'SAVE_SUCCESS',
    SET_ERROR = 'SET_ERROR',
    SET_PERSISTED_DATA = 'SET_PERSISTED_DATA'
}

type ReducerActions<T> =
    | { type: ACTION_TYPES.TOGGLE }
    | { type: ACTION_TYPES.SUBMIT }
    | { type: ACTION_TYPES.SAVE_SUCCESS; persistedData: T }
    | { type: ACTION_TYPES.SET_PERSISTED_DATA; persistedData: T }
    | { type: ACTION_TYPES.SET_ERROR; error: string }

type ActionTypes = {
    type: ACTION_TYPES
}

function getInitialState<T>(initialPersistedData: T): State<T> {
    return {
        open: false,
        saved: false,
        mustSave: false,
        error: null,
        persistedData: initialPersistedData,
        submitted: false
    }
}

function reducer<T>(state: State<T>, action: ReducerActions<T>): State<T> {
    switch (action.type) {
        case ACTION_TYPES.TOGGLE:
            // eslint-disable-next-line no-case-declarations
            const newState = {...state}
            newState.open = !state.open

            if (newState.open) newState.saved = false

            if (!open && !state.saved) {
                // onCancel
                newState.submitted = false
            }

            return {...state, ...newState}
        case ACTION_TYPES.SUBMIT:
            return {...state, submitted: true}
        case ACTION_TYPES.SET_PERSISTED_DATA:
            return {...state, persistedData: action.persistedData}
        case ACTION_TYPES.SAVE_SUCCESS:
            return {...state, saved: true, persistedData: action.persistedData, submitted: false, open: !state.open}
        case ACTION_TYPES.SET_ERROR:
            return {...state, error: action.error}

        default:
            return state
    }
}

function useWebSettingsState<T>(
    initialPersistedData: T
): {
    state: State<T>
    actions: Actions<T>
} {
    const [state, dispatch] = useReducer(reducer, getInitialState(initialPersistedData))

    const {current: actions} = useRef({
        toggle: () => dispatch({type: ACTION_TYPES.TOGGLE}),
        submit: () => dispatch({type: ACTION_TYPES.SUBMIT}),
        saveSuccess: (persistedData: T) => dispatch({type: ACTION_TYPES.SAVE_SUCCESS, persistedData}),
        setError: (error: string) => dispatch({type: ACTION_TYPES.SET_ERROR, error}),
        setPersistedData: (persistedData: T) => dispatch({type: ACTION_TYPES.SET_PERSISTED_DATA, persistedData})
    })

    return {
        state,
        actions
    } as {
        state: State<T>
        actions: typeof actions
    }
}

/**
 * Common on save handler wrapper
 *
 * @param actions
 * @param data
 * @param persist
 */
function useOnSave(actions, data, persist) {
    return useCallback(() => {
        actions.submit()
        persist(data)
            .then(() => {
                actions.saveSuccess(data)
                globalNotification.success(i18n('Settings successfully saved'))
            })
            .catch(resp => {
                actions.setError(
                    `${i18n('An error occurred while saving settings')}: ${
                        resp.data.message || resp.status || i18n('unknown')
                    }`
                )
            })
    }, [data])
}


interface SettingsFieldProps {
    setData: Dispatch<SetStateAction<any>>
    data: any
    dataKey: string
    disabled: boolean
    label: ReactElement | string
    helpText?: ReactElement | string
}

const BooleanField: FC<SettingsFieldProps> = ({setData, data, dataKey, disabled, label, helpText}) => {
    return (
        <div className="form-group">
            <label>{label}</label>
            <div className="flex">
                <div className="flex-1">
                    <ActionToggle
                        onChange={() => {
                            setData(st => ({
                                ...st,
                                [dataKey]: !data[dataKey]
                            }))
                        }}
                        elLeft={<span>on</span>}
                        elRight={<span>off</span>}
                        active={data[dataKey]}
                        disabled={disabled}
                    />
                </div>

                <div className="flex-1">
                    <div className="flex">
                        <span className="help-block">
                            <i className="fa fa-info-circle"/>
                        </span>

                        <span className="help-block">
                            <span>{helpText}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

const Alert = styled.div`
  border: 1px solid #ffe58f;
  position: relative;
  display: flex;
  margin: 0;
  padding: 8px 10px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  background-color: #fffbe6;
`

const FeatureFlags: FC = () => {
    const [data, setData] = useState(initialState)
    const {state, actions} = useWebSettingsState(initialState)
    const onSave = useOnSave(actions, data, persist)
    const [hasChanged, setHasChanged] = useState<boolean>(false)

    /* load data from backend on mount */
    useEffect(() => {
        xhr.get(RESOURCE_URL).then(({data}) => {
            setData(data)
            actions.setPersistedData(data)
        })
    }, [])

    useEffect(() => {
        const jsonData = JSON.stringify(data)
        const jsonPersisted = JSON.stringify(state.persistedData)
        setHasChanged(jsonData != jsonPersisted)
    }, [data, state])

    const fieldsDisabled = !state.open

    return (
        <WebSettingsCard
            name="pluginUpload"
            className="live-feature live-feature--pluginUpload"
            open={state.open}
            error={state.submitted ? state.error : null}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => setData(state.persistedData)}
            savedMessage={i18n('Settings successfully saved')}
        >
            <div className="live-feature__header">
                <h2 className="flex-1">{i18n('plugin-upload')}</h2>
            </div>

            <BooleanField
                setData={setData}
                data={data}
                dataKey={'enableSFTP'}
                disabled={fieldsDisabled}
                label={i18n('SFTP')}
                helpText={i18n(
                    'Enable the SFTP integration'
                )}
            />

            <BooleanField
                setData={setData}
                data={data}
                dataKey={'enablePublicApi'}
                disabled={fieldsDisabled}
                label={i18n('Public API')}
                helpText={i18n(
                    'Enable the public API (endpoints available for unauthenticated clients)'
                )}
            />

            <BooleanField
                setData={setData}
                data={data}
                dataKey={'enableAuthenticatedApi'}
                disabled={fieldsDisabled}
                label={i18n('Authenticated API')}
                helpText={i18n(
                    'Enable the authenticated API'
                )}
            />

            {!fieldsDisabled && hasChanged &&
                <div className="form-group">
                    <Alert>
                        <div>
                            The plugin "plugin-upload" must be restarted in order apply changes to these settings
                        </div>
                    </Alert>
                </div>
            }
        </WebSettingsCard>
    )
}

/**
 * Assumes `data` already with parsed values for backend.
 * @param data
 */
function persist(data: FeatureFlags) {
    return xhr.post(RESOURCE_URL, {
        enableSFTP: data.enableSFTP,
        enablePublicApi: data.enablePublicApi,
        enableAuthenticatedApi: data.enableAuthenticatedApi,
    })
}

export default {
    persist: persist,
    configComponent: FeatureFlags,
    section: i18n("Uploads"),
    name: i18n('Available features'),
    key: 'pluginUpload',
    url: RESOURCE_URL
}
